import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { procurementApi } from '@/api/procurement.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

type Workflow = Awaited<ReturnType<typeof procurementApi.listApprovalWorkflows>>[number];

export function ProcurementSetupPage() {
  const orgId = useAppSelector((s) => s.auth.user?.organizationId);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [vendorEmail, setVendorEmail] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);
  const [code, setCode] = useState('PR_DEFAULT');
  const [name, setName] = useState('Purchase request approval');
  const [documentType, setDocumentType] = useState('PURCHASE_REQUEST');
  const [minAmount, setMinAmount] = useState('0');
  const [loading, setLoading] = useState(false);
  const { rows: paginatedWorkflows, tablePaginationProps: workflowsPaginationProps } =
    useClientPaginatedRows(workflows);

  const load = () => {
    procurementApi.listApprovalWorkflows().then(setWorkflows).catch((e) => showApiError(e, 'Failed to load workflows'));
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await procurementApi.saveApprovalWorkflow({
        code,
        name,
        documentType,
        minAmount: Number(minAmount),
        stages: [
          {
            stageOrder: 1,
            name: 'Manager approval',
            requiredApprovals: 1,
            assignments: [{ assigneeType: 'PERMISSION', permissionCode: 'PROCUREMENT_REQUEST_APPROVE' }],
          },
        ],
      });
      notify.success('Approval workflow saved');
      load();
    } catch (err) {
      showApiError(err, 'Save workflow failed');
    } finally {
      setLoading(false);
    }
  };

  const sendVendorPortalLink = async () => {
    if (!orgId || !vendorEmail.trim()) {
      notify.error('Organization and vendor email required');
      return;
    }
    setPortalBusy(true);
    try {
      await procurementApi.vendorPortalRequestLink(orgId, vendorEmail.trim());
      notify.success('Magic link sent (check configured mail provider)');
    } catch (err) {
      showApiError(err, 'Failed to send vendor portal link');
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Procurement setup"
        description="Amount-based approval routing for PR, PO, and vendor bills"
        breadcrumbs={[{ label: 'Procurement', to: '/procurement' }, { label: 'Setup' }]}
      />

      <div className="card mb-6 space-y-3 p-4">
        <h3 className="font-semibold">Vendor portal</h3>
        <p className="text-sm text-slate-600">
          Suppliers submit quotations and invoices at{' '}
          <Link to="/vendor-portal/login" className="text-primary-600 hover:underline">
            /vendor-portal/login
          </Link>
          .
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <Input label="Vendor email" value={vendorEmail} onChange={(e) => setVendorEmail(e.target.value)} />
          <Button loading={portalBusy} onClick={() => void sendVendorPortalLink()}>
            Send login link
          </Button>
        </div>
      </div>

      <div className="card mb-6 space-y-3 p-4">
        <h3 className="font-semibold">New approval workflow</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} />
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Document type" value={documentType} onChange={(e) => setDocumentType(e.target.value)} />
          <Input label="Min amount" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
        </div>
        <Button loading={loading} onClick={() => void save()}>Save workflow</Button>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">Code</th>
              <th className="p-3">Name</th>
              <th className="p-3">Document</th>
              <th className="p-3">Min</th>
              <th className="p-3">Max</th>
            </tr>
          </thead>
          <tbody>
            {paginatedWorkflows.map((w) => (
              <tr key={w.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3">{w.code}</td>
                <td className="p-3">{w.name}</td>
                <td className="p-3">{w.documentType}</td>
                <td className="p-3">{w.minAmount ?? '—'}</td>
                <td className="p-3">{w.maxAmount ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination {...workflowsPaginationProps} hasRows={workflows.length > 0} />
      </div>
    </div>
  );
}
